import React, { useState, useEffect } from 'react';
import { 
  Calendar,
  Mail,
  Phone,
  Activity,
  Eye,
  Edit3,
  Send,
  MessageSquare,
  XCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

const PatientDetailsModal = ({ 
  patient, 
  onClose, 
  onEdit, 
  onSendLink, 
  onViewVitals,
  onCall, // Add onCall prop for handling patient calls
  userRole = 'admin', // 'admin' or 'doctor'
  apiClient // Pass the appropriate API client
}) => {
  const [vitalSigns, setVitalSigns] = useState([]);
  const [loadingVitals, setLoadingVitals] = useState(false);
  const [vitalSignsError, setVitalSignsError] = useState(null);
  
  // ✨ NEW: Call recording vitals state
  const [callVitals, setCallVitals] = useState(null);
  const [callRecordingHistory, setCallRecordingHistory] = useState([]);
  const [totalRecordings, setTotalRecordings] = useState(0);
  const [loadingCallVitals, setLoadingCallVitals] = useState(false);
  const [callVitalsError, setCallVitalsError] = useState(null);

  // Calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    const birthDate = new Date(dob);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return age - 1;
    }
    return age;
  };

  // Fetch patient vital signs when modal opens
  useEffect(() => {
    if (patient?.id && apiClient) {
      fetchPatientVitals(patient.id);
      // ✨ NEW: Also fetch call recording vitals
      fetchCallRecordingVitals(patient.id);
    }
  }, [patient?.id, apiClient]);

  const fetchPatientVitals = async (patientId) => {
    try {
      setLoadingVitals(true);
      setVitalSignsError(null);
      console.log('🔍 DEBUG: Fetching vitals for patient:', patientId);
      
      const response = await apiClient.getPatientVitals(patientId);
      console.log('🔍 DEBUG: API response:', response);
      console.log('🔍 DEBUG: response.data:', response.data);
      
      // Sort vital signs by creation date (newest first)
      const sortedVitals = (response.data || []).sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateB - dateA; // Descending order (newest first)
      });
      
      setVitalSigns(sortedVitals);
      console.log('🔍 DEBUG: vitalSigns state set to (sorted):', sortedVitals);
    } catch (error) {
      console.error('🔍 DEBUG: Failed to fetch patient vitals:', error);
      setVitalSignsError('Failed to load vital signs data');
      setVitalSigns([]);
    } finally {
      setLoadingVitals(false);
    }
  };

  // ✨ NEW: Fetch call recording vitals
  const fetchCallRecordingVitals = async (patientId) => {
    try {
      setLoadingCallVitals(true);
      setCallVitalsError(null);
      console.log('📞 DEBUG: Fetching call recording vitals for patient:', patientId);
      
      // Check if the apiClient has the new call vitals method
      if (apiClient.getPatientCallVitals) {
        const response = await apiClient.getPatientCallVitals(patientId);
        console.log('📞 DEBUG: Call vitals API response:', response);
        
        if (response.data?.success && response.data?.has_call_vitals) {
          setCallVitals(response.data.vital_signs);
          setCallRecordingHistory(response.data.recording_history || []);
          setTotalRecordings(response.data.total_recordings || 0);
          console.log('📞 DEBUG: Call vitals found:', response.data.vital_signs);
          console.log('📞 DEBUG: Recording history:', response.data.recording_history);
          console.log('📞 DEBUG: Total recordings:', response.data.total_recordings);
        } else {
          setCallVitals(null);
          setCallRecordingHistory([]);
          setTotalRecordings(0);
          console.log('📞 DEBUG: No call vitals found for patient');
        }
      } else {
        console.log('📞 DEBUG: Call vitals API not available');
        setCallVitals(null);
      }
    } catch (error) {
      console.error('📞 DEBUG: Failed to fetch call vitals:', error);
      setCallVitalsError('Failed to load call recording vitals');
      setCallVitals(null);
      setCallRecordingHistory([]);
      setTotalRecordings(0);
    } finally {
      setLoadingCallVitals(false);
    }
  };

  if (!patient) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-[#F9FBFC] rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-[#DCEAF5]">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-[#0B1C2C]">Patient Details</h2>
            <button
              onClick={onClose}
              className="text-[#3D3D3D] hover:text-[#207CE5]"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Left Side - Basic Information and Contact Information */}
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-[#0B1C2C]">Basic Information</h3>
                
                <div className="bg-[#DCEAF5] p-4 rounded-lg">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#207CE5] to-[#4682B4] rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-xl">
                        {patient.alias?.split(' ').map(n => n[0]).join('') || 'P'}
                      </span>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-[#0B1C2C]">{patient.alias}</h4>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-[#3D3D3D]">Date of Birth</label>
                      <p className="text-sm text-[#0B1C2C]">
                        {patient.dob ? new Date(patient.dob).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#3D3D3D]">Age</label>
                      <p className="text-sm text-[#0B1C2C]">{calculateAge(patient.dob)} years</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#3D3D3D]">Gender</label>
                      <p className="text-sm text-[#0B1C2C]">{patient.gender || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#3D3D3D]">Status</label>
                      <p className="text-sm text-[#0B1C2C]">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#DCEAF5] text-[#008080]">
                          {patient.status}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-[#0B1C2C]">Contact Information</h3>
                
                <div className="bg-[#DCEAF5] p-4 rounded-lg space-y-3">
                  {patient.email && (
                    <div className="flex items-start space-x-3">
                      <Mail className="h-5 w-5 text-[#3D3D3D] mt-0.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <label className="text-sm font-medium text-[#3D3D3D]">Email</label>
                        <p className="text-sm text-[#0B1C2C] break-words">{patient.email}</p>
                      </div>
                    </div>
                  )}
                  
                  {patient.phone && (
                    <div className="flex items-center space-x-3">
                      <Phone className="h-5 w-5 text-[#3D3D3D] flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <label className="text-sm font-medium text-[#3D3D3D]">Phone</label>
                        <p className="text-sm text-[#0B1C2C]">{patient.phone}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5 text-[#3D3D3D] flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <label className="text-sm font-medium text-[#3D3D3D]">Registered</label>
                      <p className="text-sm text-[#0B1C2C]">
                        {patient.created_at ? new Date(patient.created_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Account Status */}
                <div className="bg-[#DCEAF5] p-4 rounded-lg">
                  <h4 className="font-medium text-[#0B1C2C] mb-2">Account Details</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-[#3D3D3D]">User Account</span>
                      <span className={`text-sm font-medium ${
                        patient.has_user_account ? 'text-[#008080]' : 'text-[#00BCD4]'
                      }`}>
                        {patient.has_user_account ? 'Active' : 'Not Created'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-[#3D3D3D]">Organization</span>
                      <span className="text-sm text-[#0B1C2C]">{patient.tenant_name}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

                         {/* Right Side - Recent Recordings & Call Vitals */}
             <div className="space-y-4 min-w-0">
               <h3 className="text-lg font-medium text-[#0B1C2C]">Vital Signs Data</h3>

               {/* Manual Recording Vitals Section - MOVED TO TOP */}
              <div className="bg-[#DCEAF5] p-4 rounded-lg max-h-96 overflow-y-auto">
                
                {loadingVitals ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#207CE5] mx-auto mb-2"></div>
                    <p className="text-sm text-[#3D3D3D]">Loading vital signs...</p>
                  </div>
                ) : vitalSignsError ? (
                  <div className="text-center py-8">
                    <div className="text-red-500 text-6xl mb-2">⚠️</div>
                    <p className="text-sm text-red-600 mb-2">Error loading vital signs</p>
                    <p className="text-xs text-[#3D3D3D]">{vitalSignsError}</p>
                  </div>
                                 ) : vitalSigns.length > 0 ? (
                   <div className="space-y-4">
                                                                 {/* Recent Manual Recordings - Simple 5-column layout without scrolling (Newest First) */}
                      <div>
                        <h4 className="text-sm font-medium text-[#0B1C2C] mb-2 flex items-center">
                          <Activity className="h-4 w-4 mr-2 text-[#008080]" />
                          Web App Recordings
                        </h4>
                        <div className="space-y-1.5 max-h-48 overflow-y-auto">
                          {/* Latest Recording - Highlighted */}
                          {vitalSigns.length > 0 && (
                            <div className="bg-white p-2 rounded border border-[#B3E5D1] border-l-4 border-l-[#008080]">
                              <div className="mb-1 flex items-center justify-between">
                                <span className="font-medium text-[#0B1C2C] text-xs">
                                  {new Date(vitalSigns[0].created_at).toLocaleDateString()}
                                </span>
                                <span className="text-xs text-[#008080] bg-[#E8F5F0] px-2 py-1 rounded font-medium">
                                  LATEST
                                </span>
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 text-[#3D3D3D] text-xs">
                                <span>HR: {vitalSigns[0].hr || vitalSigns[0].heart_rate || 'N/A'}</span>
                                <span>BP: {vitalSigns[0].bp_sys && vitalSigns[0].bp_dia 
                                  ? `${vitalSigns[0].bp_sys}/${vitalSigns[0].bp_dia}` 
                                  : vitalSigns[0].blood_pressure_systolic && vitalSigns[0].blood_pressure_diastolic
                                  ? `${vitalSigns[0].blood_pressure_systolic}/${vitalSigns[0].blood_pressure_diastolic}`
                                  : 'N/A'}</span>
                                <span>HRV: {vitalSigns[0].hrv || vitalSigns[0].heart_rate_variability 
                                  ? (vitalSigns[0].hrv || vitalSigns[0].heart_rate_variability).toFixed(1) 
                                  : 'N/A'}</span>
                                <span>Rhythm: {vitalSigns[0].rhythm || 'N/A'}</span>
                              </div>
                            </div>
                          )}
                          
                          {/* Previous Recordings */}
                          {vitalSigns.length > 1 && (
                            <div>
                              <h5 className="text-xs font-medium text-[#666] mb-2 uppercase tracking-wide">
                                Previous Recordings
                              </h5>
                              {vitalSigns.slice(1).map((vital, index) => (
                                <div key={vital.id} className="bg-white p-1.5 rounded border border-[#E5E7EB] text-xs mb-1.5">
                                  <div className="mb-1">
                                    <span className="font-medium text-[#0B1C2C] text-xs">
                                      {new Date(vital.created_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 text-[#3D3D3D] text-xs">
                                    <span>HR: {vital.hr || vital.heart_rate || 'N/A'}</span>
                                    <span>BP: {vital.bp_sys && vital.bp_dia 
                                      ? `${vital.bp_sys}/${vital.bp_dia}` 
                                      : vital.blood_pressure_systolic && vital.blood_pressure_diastolic
                                      ? `${vital.blood_pressure_systolic}/${vital.blood_pressure_diastolic}`
                                      : 'N/A'}</span>
                                    <span>HRV: {vital.hrv || vital.heart_rate_variability 
                                      ? (vital.hrv || vital.heart_rate_variability).toFixed(1) 
                                      : 'N/A'}</span>
                                    <span>Rhythm: {vital.rhythm || 'N/A'}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                   </div>
                ) : (
                  <div className="text-center py-4">
                    <Activity className="h-6 w-6 text-[#3D3D3D] mx-auto mb-2" />
                    <p className="text-sm text-[#3D3D3D]">No vital signs recorded yet</p>
                    <p className="text-xs text-[#3D3D3D] mt-1">Send a recording link to get started</p>
                  </div>
                )}
              </div>

               {/* ✨ Call Recording Vitals Section - MOVED TO BOTTOM */}
               {callRecordingHistory.length > 0 && (
                 <div className="bg-[#E8F4FD] p-4 rounded-lg border-2 border-[#3498DB] max-h-96 overflow-y-auto">
                   <h4 className="text-sm font-medium text-[#0B1C2C] mb-3 flex items-center">
                     <Phone className="h-4 w-4 mr-2 text-[#3498DB]" />
                     Call Recording
                   </h4>
                   
                   <div className="space-y-2 max-h-48 overflow-y-auto">
                     {/* Latest Recording - Highlighted */}
                     {callVitals && (
                       <div className="bg-white p-2 rounded border border-[#B3D9FF] border-l-4 border-l-[#3498DB]">
                         <div className="mb-1 flex items-center justify-between">
                           <span className="font-medium text-[#0B1C2C] text-xs">
                             {callVitals.recorded_at ? new Date(callVitals.recorded_at).toLocaleDateString() : 'Recent'}
                           </span>
                           <span className="text-xs text-[#3498DB] bg-[#EBF8FF] px-2 py-1 rounded font-medium">
                             LATEST
                           </span>
                         </div>
                         <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 text-[#3D3D3D] text-xs">
                           <span>HR: {callVitals.heart_rate || 'N/A'}</span>
                           <span>BP: {
                             callVitals.systolic_bp && callVitals.diastolic_bp 
                               ? `${callVitals.systolic_bp}/${callVitals.diastolic_bp}` 
                               : 'N/A'
                           }</span>
                           <span>HRV: {
                             callVitals.hrv && !isNaN(parseFloat(callVitals.hrv)) ? parseFloat(callVitals.hrv).toFixed(1) : 'N/A'
                           }</span>
                           <span>Rhythm: {callVitals.rhythm_regularity || 'N/A'}</span>
                         </div>
                         {callVitals.confidence_score && (
                           <div className="mt-1 text-xs text-[#666]">
                             <span className="font-medium">Confidence:</span> {(callVitals.confidence_score * 100).toFixed(1)}%
                           </div>
                         )}
                       </div>
                     )}
                     
                     {/* Previous Recordings */}
                     {callRecordingHistory.length > 1 && (
                       <div>
                         <h5 className="text-xs font-medium text-[#666] mb-2 uppercase tracking-wide">
                           Previous Recordings
                         </h5>
                         {/* Show ALL recordings - let scroller handle overflow */}
                         {callRecordingHistory.slice(1).map((recording, index) => (
                           <div key={recording.call_recording_id || index} className="bg-white p-2 rounded border border-[#E5E7EB] text-xs mb-1.5">
                             <div className="mb-1 flex items-center justify-between">
                               <span className="font-medium text-[#0B1C2C]">
                                 {recording.recorded_at ? new Date(recording.recorded_at).toLocaleDateString() : 'Unknown Date'}
                               </span>
                               <span className="text-xs text-[#666]">
                                 {recording.recorded_at ? new Date(recording.recorded_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}
                               </span>
                             </div>
                             <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 text-[#3D3D3D]">
                               <span>HR: {recording.heart_rate || 'N/A'}</span>
                               <span>BP: {
                                 recording.systolic_bp && recording.diastolic_bp 
                                   ? `${recording.systolic_bp}/${recording.diastolic_bp}` 
                                   : 'N/A'
                               }</span>
                               <span>HRV: {recording.hrv && !isNaN(parseFloat(recording.hrv)) ? parseFloat(recording.hrv).toFixed(1) : 'N/A'}</span>
                               <span>Rhythm: {recording.rhythm_regularity || 'N/A'}</span>
                             </div>
                           </div>
                         ))}
                       </div>
                     )}
                   </div>
                 </div>
               )}

               {/* Loading state for call vitals */}
               {loadingCallVitals && (
                 <div className="bg-[#E8F4FD] p-4 rounded-lg border-2 border-[#3498DB]">
                   <div className="text-center py-4">
                     <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3498DB] mx-auto mb-2"></div>
                     <p className="text-xs text-[#3498DB]">Loading call vitals...</p>
                   </div>
                 </div>
               )}
            </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="border-t border-[#DCEAF5] p-6">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-[#3D3D3D] bg-[#F9FBFC] border border-[#DCEAF5] rounded-lg hover:bg-[#DCEAF5]"
            >
              Close
            </button>
            {/* View Vitals button commented out for now
            {onViewVitals && (
              <button
                onClick={() => onViewVitals(patient)}
                className="px-4 py-2 text-sm font-medium text-white bg-[#00BCD4] border border-transparent rounded-lg hover:bg-[#008080]"
              >
                View Vitals
              </button>
            )}
            */}
            {onCall && (
              <button
                onClick={() => onCall(patient)}
                className="px-4 py-2 text-sm font-medium text-white bg-[#008080] border border-transparent rounded-lg hover:bg-[#00BCD4]"
              >
                Call
              </button>
            )}
            {onSendLink && (
              <button 
                onClick={() => onSendLink(patient)}
                className="px-4 py-2 text-sm font-medium text-white bg-[#207CE5] border border-transparent rounded-lg hover:bg-[#4682B4]"
              >
                Send Recording Link
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientDetailsModal;
